<script setup lang="ts">
const { t: $t } = useI18n()

import { useRouteQuery } from '@vueuse/router'
import type { DataTableColumns } from 'naive-ui'
import type { ApiQuickSaleGetAll, ApiQuickSaleGetAllByMachineryId } from '~/types'
import { NuxtLink } from '#components'

const props = defineProps<{
  machineryId?: string
  view: 'machinery' | 'sale'
}>()

const isMachineryView = computed(() => props.view === 'machinery')
const isSaleView = computed(() => props.view === 'sale')

const { t } = useI18n()

// Sale view search, filters and pagination
const fulltextSearch = {
  filters: ['/machineryId', '/machinery/type/name', '/description', '/createdBy/name'],
  placeholder: t('quickSale.overview.sale.search.placeholder'),
  queryId: 'quickSale',
}
const { fulltextSearchValue, where: fulltextSearchWhere } = useFilterData(fulltextSearch)

const showQuickSalesWithSoldMachinery = useRouteQueryAsBoolean('showQuickSalesWithSoldMachinery', false)
const selectedMachineryId = useRouteQuery<string | null>('machineryId', null)
const selectedMachineryTypeId = useRouteQuery<string | null>('machineryTypeId', null)
const machineryProducerCompanyName = useRouteQuery<string | null>('machineryProducerCompanyName', null)
const selectedMachineryDriveId = useRouteQuery<string | null>('machineryDriveId', null)
const minLiftingWeight = useRouteQueryAsNumber('minLiftingWeight')
const maxLiftingWeight = useRouteQueryAsNumber('maxLiftingWeight')
const description = useRouteQuery<string | null>('description', null)
const createdBy = useRouteQuery<string | null>('createdBy', null)
const createdAtRange = useRouteQueryAsNumberRange('createdAtRange')

function resetFilters() {
  showQuickSalesWithSoldMachinery.value = false
  selectedMachineryId.value = null
  selectedMachineryTypeId.value = null
  machineryProducerCompanyName.value = null
  selectedMachineryDriveId.value = null
  minLiftingWeight.value = null
  maxLiftingWeight.value = null
  description.value = null
  createdBy.value = null
  createdAtRange.value = null
}

const saleViewWhere = computed(() => ({
  ...fulltextSearchWhere.value,
  machineryId: selectedMachineryId.value ? { contains: selectedMachineryId.value, mode: 'insensitive' } : undefined,
  description: description.value ? { contains: description.value, mode: 'insensitive' } : undefined,
  createdAt: createdAtRange.value ? createPrismaDateRangeFilterSerialized(createdAtRange.value, { transformStartToStartOfDay: false }) : undefined,
  createdBy: createdBy.value
    ? {
        OR: [
          { name: { contains: createdBy.value, mode: 'insensitive' } },
          { email: { contains: createdBy.value, mode: 'insensitive' } },
        ],
      }
    : undefined,
  machinery: {
    typeId: selectedMachineryTypeId.value ?? undefined,
    producerCompanyName: machineryProducerCompanyName.value ? { contains: machineryProducerCompanyName.value, mode: 'insensitive' } : undefined,
    receptionMachinery: {
      driveId: selectedMachineryDriveId.value ?? undefined,
      liftingWeight: minLiftingWeight.value || maxLiftingWeight.value
        ? {
            ...generatePrismaGreaterThanOrEqualWhere(minLiftingWeight.value),
            ...generatePrismaLessThanOrEqualWhere(maxLiftingWeight.value),
          }
        : undefined,
    },
    soldAt: showQuickSalesWithSoldMachinery.value ? undefined : null,
  },
}))

const saleViewTablePage = useRouteQuery<number>('page', 1, { transform: Number })
const {
  serverSidePaginationTableProps: saleViewServerSidePaginationTableProps,
  serverSidePagination: saleViewServerSidePagination,
  watchForPageCount: saleViewWatchForPageCount,
  watchForReset: saleViewWatchForReset,
} = useTablePagination({ pageCount: 1, page: saleViewTablePage.value })
saleViewWatchForReset(saleViewWhere)

// Queries
const { quickSale, machineryType, machineryDrive } = useQuery()

const { data: quickSalesByMachineryId, isLoading: isLoadingQuickSalesByMachineryId } = quickSale.allByMachineryId(props.machineryId, isMachineryView.value)
const { data: quickSales, isLoading: isLoadingQuickSales } = quickSale.all(saleViewWhere, saleViewServerSidePagination, isSaleView.value)
saleViewWatchForPageCount(computed(() => quickSales.value?.totalCount ?? 0))

const { data: machineryTypes } = machineryType.all()
const { data: machineryDrives } = machineryDrive.all()

// Options
const machineryTypeOptions = computed(() => machineryTypes.value?.map(type => ({
  label: type.name,
  value: type.id,
})))
const machineryDriveOptions = computed(() => machineryDrives.value?.map(drive => ({
  label: $t(`machinery.drive.${drive.name}`),
  value: drive.id,
})))

// Columns
const quickSaleByMachineryIdColumns: DataTableColumns<ApiQuickSaleGetAllByMachineryId> = [
  {
    title: t('common.field.description.name'),
    key: 'description',
    minWidth: 150,
  },
  {
    title: t('common.field.createdAt.name'),
    key: 'createdAt',
    render: ({ createdAt }) => useDateAsString(createdAt),
  },
  {
    title: t('common.field.createdBy.name'),
    key: 'createdBy',
    render: ({ createdBy, createdByEmail }) => createdBy.name ?? createdByEmail,
  },
]

const quickSaleColumns: DataTableColumns<ApiQuickSaleGetAll> = [
  {
    title: t('offer.field.machineryId.name'),
    key: 'machineryId',
    render: ({ machineryId }) => h(NuxtLink, { to: `/machinery/${machineryId}`, class: 'text-blue-500 underline' }, machineryId),
  },
  {
    title: t('machinery.type.name'),
    key: 'machinery',
    render: ({ machinery }) => machinery.type.name,
  },
  {
    title: t('machinery.field.producerCompanyName.name'),
    key: 'machinery',
    render: ({ machinery }) => machinery.producerCompanyName,
  },
  {
    title: t('machinery.field.drive.name'),
    key: 'machinery',
    render: ({ machinery }) => machinery.receptionMachinery?.drive ? t(`machinery.drive.${machinery.receptionMachinery.drive.name}`) : t('common.notAvailable'),
  },
  {
    title: t('machinery.field.liftingWeight.name'),
    key: 'machinery',
    render: ({ machinery }) => machinery.receptionMachinery?.liftingWeight ?? t('common.notAvailable'),
  },
  {
    title: t('common.field.description.name'),
    key: 'description',
    minWidth: 150,
  },
  {
    title: t('common.field.createdAt.name'),
    key: 'createdAt',
    render: ({ createdAt }) => useDateAsString(createdAt),
  },
  {
    title: t('common.field.createdBy.name'),
    key: 'createdBy',
    render: ({ createdBy, createdByEmail }) => createdBy.name ?? createdByEmail,
  },
]

// Mutations
const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const deleteOne = useMutation({
  mutationFn: $trpc.quickSale.delete.mutate,
  onError: makeTrpcErrorToast(notification, { description: t('quickSale.overview.notification.delete.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['machines'] }),
      queryClient.invalidateQueries({ queryKey: ['quickSales'] }),
    ])
    popupConfirmDelete.value = null
    notification.success({ title: t('quickSale.overview.notification.delete.error'), duration: 4500 })
  },
})

// Popups
const popupConfirmDelete = ref<null | { id: string }>(null)
const popupPdf = ref<null | { id: string, description: string }>(null)
</script>

<template>
  <TheConfirmPopup v-if="popupConfirmDelete" @confirm="deleteOne.mutate(popupConfirmDelete)" @close="popupConfirmDelete = null">
    {{ $t('quickSale.overview.confirmDelete') }}
  </TheConfirmPopup>
  <QuickSalePopupPdf
    v-if="popupPdf"
    :payload="popupPdf"
    @close="popupPdf = null"
  />

  <TableFilters v-if="isSaleView" v-model="fulltextSearchValue" :placeholder="fulltextSearch.placeholder">
    <n-collapse class="mb-2">
      <n-collapse-item>
        <template #header>
          <span class="text-lg">
            {{ $t('quickSale.overview.expandFilters') }}
          </span>
        </template>
        <div class="flex flex-col gap-2 bg-gray-200 p-3 rounded-sm">
          <n-checkbox v-model:checked="showQuickSalesWithSoldMachinery" type="checkbox">
            {{ $t('quickSale.overview.sale.filter.showQuickSalesWithSoldMachinery') }}
          </n-checkbox>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <p class="font-semibold">
                {{ $t('offer.field.machineryId.name') }}
              </p>
              <n-input v-model:value="selectedMachineryId" :consistent-menu-width="false" clearable />
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('machinery.type.name') }}
              </p>
              <n-select
                v-model:value="selectedMachineryTypeId"
                :options="machineryTypeOptions"
                :consistent-menu-width="false"
                filterable
                clearable
              />
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('machinery.field.producerCompanyName.name') }}
              </p>
              <n-input v-model:value="machineryProducerCompanyName" :consistent-menu-width="false" clearable />
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('machinery.field.drive.name') }}
              </p>
              <n-select
                v-model:value="selectedMachineryDriveId"
                :options="machineryDriveOptions"
                :consistent-menu-width="false"
                filterable
                clearable
              />
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('machinery.field.liftingWeight.name') }}
              </p>
              <div class="flex items-center gap-2">
                <n-input-number
                  v-model:value="minLiftingWeight"
                  :min="0"
                  :consistent-menu-width="false"
                  placeholder="50"
                  clearable
                >
                  <template #prefix>
                    min.
                  </template>
                </n-input-number>
                <n-input-number
                  v-model:value="maxLiftingWeight"
                  :min="0"
                  :consistent-menu-width="false"
                  placeholder="50"
                  clearable
                >
                  <template #prefix>
                    max.
                  </template>
                </n-input-number>
              </div>
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('common.field.description.name') }}
              </p>
              <n-input v-model:value="description" :consistent-menu-width="false" clearable />
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('common.field.createdAt.name') }}
              </p>
              <n-date-picker
                v-model:value="createdAtRange"
                type="daterange"
                clearable
              />
            </div>
            <div>
              <p class="font-semibold">
                {{ $t('common.field.createdBy.name') }}
              </p>
              <n-input v-model:value="createdBy" :consistent-menu-width="false" clearable />
            </div>
            <div class="flex items-end">
              <n-button class="!bg-white w-full md:w-auto" @click="resetFilters">
                {{ $t('common.resetAll') }}
              </n-button>
            </div>
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
    <TableView
      v-bind="saleViewServerSidePaginationTableProps"
      :data="quickSales?.data"
      :columns="quickSaleColumns"
      :is-loading="isLoadingQuickSales"
      :action-buttons="() => ['pdf', 'delete']"
      @pdf="row => popupPdf = row"
      @delete="row => popupConfirmDelete = row"
      @update:page="(page) => {
        saleViewServerSidePaginationTableProps['onUpdate:page'](page)
        saleViewTablePage = page
      }"
    />
  </TableFilters>

  <TableView
    v-else
    :data="quickSalesByMachineryId"
    :columns="quickSaleByMachineryIdColumns"
    :is-loading="isLoadingQuickSalesByMachineryId"
    :action-buttons="() => ['pdf', 'delete']"
    @pdf="row => popupPdf = row"
    @delete="row => popupConfirmDelete = row"
  />
</template>
